/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Launcher } from '@/types/models';
import { CreateLauncherDialog } from './CreateLauncherDialog';
import { EditLauncherDialog } from './EditLauncherDialog';
import { RotateLauncherDialog } from './RotateLauncherDialog';
import { useStoreContext } from '@/contexts/StoreContext';
import { CopyText } from 'ogc-processes-components';
import { DateTime } from 'luxon';
import { ButtonGroup } from 'primereact/buttongroup';
import { UserGroupsBanner } from './UserGroupsBanner';

interface LauncherDialogProps {
  visible: boolean;
  onHide: () => void;
}

export const LauncherDialog: React.FC<LauncherDialogProps> = ({ visible, onHide }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [rotateDialogVisible, setRotateDialogVisible] = useState(false);
  const [selectedLauncherName, setSelectedLauncherName] = useState('');
  const [selectedLauncher, setSelectedLauncher] = useState<Launcher | null>(null);
  const { antflowProcessApiEndpoint } = useStoreContext();

  if (antflowProcessApiEndpoint === undefined) {
    throw Error('antflowProcessApiEndpoint may be not undefined to fetch user launcher');
  }

  const {
    data: launchers,
    isPending,
    refetch
  } = useQuery({
    queryKey: ['launchers'],
    queryFn: () => antflowProcessApiEndpoint.list_launchers(),
    enabled: visible,
    refetchOnWindowFocus: false
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['launchers'] });
    refetch();
  };

  const handleRotate = (launcherName: string) => {
    setSelectedLauncherName(launcherName);
    setRotateDialogVisible(true);
  };

  const handleEdit = (launcher: Launcher) => {
    setSelectedLauncher(launcher);
    setEditDialogVisible(true);
  };

  const deleteMutation = useMutation({
    mutationFn: (launcherID: string) => antflowProcessApiEndpoint.delete_launcher(launcherID),
    onSuccess: () => handleRefresh()
  });

  const handleDelete = (launcherID: string) => {
    confirmDialog({
      message: t(['launchers.delete_confirm', 'Are you sure you want to delete this launcher?']),
      header: t(['launchers.delete_title', 'Delete Launcher']),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => deleteMutation.mutate(launcherID)
    });
  };

  const statusBodyTemplate = (rowData: Launcher) => {
    if (rowData.expired) {
      return <Tag severity="danger" value={t(['launchers.status.expired', 'Expired'])} />;
    }
    if (rowData.active) {
      return <Tag severity="success" value={t(['launchers.status.active', 'Active'])} />;
    }
    return <Tag severity="warning" value={t(['launchers.status.inactive', 'Inactive'])} />;
  };

  const actionsBodyTemplate = (rowData: Launcher) => {
    return (
      <ButtonGroup>
        <Button
          icon="pi pi-refresh"
          severity="secondary"
          size="small"
          tooltip={t(['launchers.rotate', 'Rotate Token'])}
          tooltipOptions={{ position: 'top' }}
          onClick={() => handleRotate(rowData.name)}
        />
        <Button
          icon="pi pi-pencil"
          severity="info"
          size="small"
          tooltip={t(['launchers.edit', 'Edit'])}
          tooltipOptions={{ position: 'top' }}
          onClick={() => handleEdit(rowData)}
        />
        <Button
          icon="pi pi-trash"
          severity="danger"
          size="small"
          tooltip={t(['launchers.delete', 'Delete'])}
          tooltipOptions={{ position: 'top' }}
          onClick={() => handleDelete(rowData.id)}
          loading={deleteMutation.isPending}
        />
      </ButtonGroup>
    );
  };

  const dateBodyTemplate = (date: string | null, locale: string) => {
    if (!date) {
      return '-';
    }
    return DateTime.fromISO(date).setLocale(locale).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  };

  const typeBodyTemplate = (user_id: string | null) => {
    if (!user_id) {
      return 'Transverse';
    }
    return 'User';
  };

  const idBodyTemplate = (id: string) => {
    return <CopyText text={id} textStyle={{ maxWidth: '150px' }} />;
  };
  const flavorsBodyTemplate = (rowData: Launcher) => {
    if (!rowData.flavors || rowData.flavors.length === 0) {
      return <span className="text-color-secondary">-</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {rowData.flavors.map((flavor, idx) => (
          <Tag key={idx} value={flavor} />
        ))}
      </div>
    );
  };

  const groupsBodyTemplate = (rowData: Launcher) => {
    if (!rowData.groups || rowData.groups.length === 0) {
      return <span className="text-color-secondary">-</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {rowData.groups.map((group, idx) => (
          <Tag key={idx} className="text-xs font-bold" value={group} />
        ))}
      </div>
    );
  };

  const header = (
    <div className="flex flex-column gap-1">
      <UserGroupsBanner className="pb-2" />
      <div className="flex justify-content-between align-items-center">
        <span className="text-lg font-semibold">{t(['launchers.list_title', 'Launchers'])}</span>
        <div className="flex gap-2">
          <Button
            icon="pi pi-refresh"
            severity="secondary"
            size="small"
            onClick={handleRefresh}
            tooltip={t(['actions.refresh', 'Refresh'])}
            tooltipOptions={{ position: 'top' }}
          />
          <Button
            icon="pi pi-plus"
            label={t(['launchers.new', 'New Launcher'])}
            size="small"
            onClick={() => setCreateDialogVisible(true)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ConfirmDialog />
      <Dialog
        header={t(['launchers.title', 'Manage Launchers'])}
        visible={visible}
        style={{ width: '80vw', maxWidth: '1200px' }}
        onHide={onHide}
        maximizable
      >
        <DataTable
          value={launchers}
          loading={isPending}
          header={header}
          emptyMessage={t(['launchers.no_launchers', 'No launchers found'])}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          stripedRows
          size="small"
        >
          <Column field="id" header="ID" style={{ maxWidth: '150px' }} body={(rowData) => idBodyTemplate(rowData.id)} />
          <Column field="name" header={t(['launchers.name', 'Name'])} sortable />
          <Column field="flavors" header={t(['launchers.flavors', 'Flavors'])} body={flavorsBodyTemplate} />
          <Column field="groups" header={t(['launchers.groups', 'Groups'])} body={groupsBodyTemplate} />
          <Column header="Type" className="text-sm" body={(rowData) => typeBodyTemplate(rowData.user_id)} sortable />
          <Column
            field="last_seen"
            header={t(['launchers.last_seen', 'Last Seen'])}
            body={(rowData) => dateBodyTemplate(rowData.last_seen, i18n.language)}
            sortable
          />
          <Column
            field="expiration_date"
            header={t(['launchers.expiration_date', 'Expiration'])}
            body={(rowData) => dateBodyTemplate(rowData.expiration_date, i18n.language)}
            sortable
          />
          <Column
            header={t(['launchers.status_header', 'Status'])}
            body={statusBodyTemplate}
            style={{ width: '100px' }}
          />
          <Column
            header={t(['processes.properties.actions', 'Actions'])}
            body={actionsBodyTemplate}
            style={{ width: '150px' }}
          />
        </DataTable>
      </Dialog>
      <CreateLauncherDialog
        visible={createDialogVisible}
        onHide={() => setCreateDialogVisible(false)}
        onSuccess={handleRefresh}
        apiEndpoint={antflowProcessApiEndpoint}
      />
      {selectedLauncher && (
        <EditLauncherDialog
          visible={editDialogVisible}
          launcher={selectedLauncher}
          onHide={() => setEditDialogVisible(false)}
          onSuccess={handleRefresh}
          apiEndpoint={antflowProcessApiEndpoint}
        />
      )}
      <RotateLauncherDialog
        visible={rotateDialogVisible}
        launcherName={selectedLauncherName}
        onHide={() => setRotateDialogVisible(false)}
        onSuccess={handleRefresh}
        apiEndpoint={antflowProcessApiEndpoint}
      />
    </>
  );
};
